import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Link as LinkIcon, LogOut, MoreHorizontal, Pencil } from "lucide-react";
import type { PlatformTypeName } from "@multi-chat/shared";
import { disconnect as disconnectWhatsApp } from "../../api/whatsapp-api.js";
import { disconnectFacebook } from "../../api/facebook-api.js";
import { disconnectInstagram } from "../../api/instagram-api.js";
import { ChannelMark } from "../../components/channel/ChannelMark.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import { AppNotice } from "../../components/common/AppNotice.js";
import { ConfirmDisconnectDialog } from "../../components/common/ConfirmDisconnectDialog.js";
import { ListSkeleton } from "../../components/common/WorkspaceLoader.js";
import { CHANNEL_EMPTY_ARTWORK } from "./channel-artwork.js";
import { channelCardTitle } from "./channel-local-name.js";
import { whatsAppProviderTitle } from "../whatsapp/whatsapp-channel-title.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { useNotifications } from "../notifications/notification-context.js";
import { formatInstagramUsername } from "../instagram/instagram-connection-presentation.js";
import {
  facebookPageUrl,
  instagramConversationUrl,
  nextOpenMenuId,
  whatsAppConversationUrl,
  writeClipboardText,
} from "./channel-list-menu.js";
import {
  channelStatusLabelKey,
  isChannelSidebarVisible,
  type ChannelPlatformId,
} from "./channel-connection-policy.js";
import { useChannelDirectory } from "./ChannelsWorkspaceLayout.js";

const PLATFORM_TYPE: Record<ChannelPlatformId, PlatformTypeName> = {
  whatsapp: "WHATSAPP",
  facebook: "FACEBOOK",
  instagram: "INSTAGRAM",
};

function statusVariant(state: string): "default" | "success" | "warning" | "error" {
  if (state === "CONNECTED") return "success";
  if (state === "ERROR") return "error";
  if (state === "PENDING_PHONE_SELECTION" || state === "PENDING_PAGE_SELECTION") return "warning";
  return "default";
}

export function ChannelsHomePage(): React.JSX.Element {
  const { t } = useTranslation();
  const directory = useChannelDirectory();
  const rows = channelRows(directory, t);
  const [openMenuId, setOpenMenuId] = useState<ChannelPlatformId | null>(null);

  function onMenuOpenChange(id: ChannelPlatformId, open: boolean): void {
    setOpenMenuId((current) => nextOpenMenuId(current, id, open));
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="channels-home">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-10 md:px-8 md:py-12">
        <div className="mx-auto w-full max-w-[52rem]">
          {directory.loading ? (
            <ListSkeleton rows={3} label={t("channels.home.loading")} testId="channels-loading" />
          ) : directory.error ? (
            <AppNotice
              severity="error"
              testId="channels-load-error"
              description={t("channels.loadError")}
              action={
                <Button type="button" size="sm" variant="outline" onClick={directory.reload}>
                  {t("channels.retry")}
                </Button>
              }
            />
          ) : rows.length === 0 ? (
            <ChannelsEmptyState />
          ) : (
            <ul className="m-0 grid list-none gap-3 p-0" data-testid="channels-home-list">
              {rows.map((row) => (
                <ChannelListCard
                  key={row.id}
                  row={row}
                  menuOpen={openMenuId === row.id}
                  returnFocusOnClose={openMenuId === null || openMenuId === row.id}
                  onMenuOpenChange={(open) => onMenuOpenChange(row.id, open)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ChannelsEmptyState(): React.JSX.Element {
  const { t } = useTranslation();
  const directory = useChannelDirectory();

  return (
    <div
      className="mx-auto grid w-full max-w-lg justify-items-center px-2 py-8 text-center"
      data-testid="channels-home-empty"
    >
      <img
        src={CHANNEL_EMPTY_ARTWORK}
        alt=""
        width={1254}
        height={1254}
        className="h-auto w-[min(18rem,72vw)] object-contain"
      />
      <div className="mt-6 grid max-w-md gap-2">
        <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.02em] text-foreground">
          {t("channels.home.emptyTitle")}
        </h2>
        <p className="m-0 text-[0.875rem] leading-relaxed text-muted">{t("channels.home.emptyHelp")}</p>
      </div>
      <Button
        type="button"
        size="sm"
        className="mt-5"
        onClick={directory.openAddChannel}
        data-testid="channels-empty-add"
      >
        {t("channels.home.emptyAction")}
      </Button>
    </div>
  );
}

type ChannelRow = {
  id: ChannelPlatformId;
  state: string;
  title: string;
  secondary: string | null;
  providerId: string | null;
  conversationLink: string | null;
  facebookPageLink: string | null;
  canDisconnect: boolean;
};

function channelRows(
  directory: ReturnType<typeof useChannelDirectory>,
  t: (key: string) => string,
): ChannelRow[] {
  const rows: ChannelRow[] = [];
  if (directory.whatsapp && isChannelSidebarVisible(directory.whatsapp.state)) {
    rows.push({
      id: "whatsapp",
      state: directory.whatsapp.state,
      title: whatsAppProviderTitle(
        directory.whatsapp.workspacePhone?.displayName,
        t("channels.platforms.whatsapp"),
      ),
      secondary: directory.whatsapp.workspacePhone?.phoneNumber ?? null,
      providerId: directory.whatsapp.workspacePhone?.externalPhoneNumberId ?? null,
      conversationLink: whatsAppConversationUrl(directory.whatsapp.workspacePhone?.phoneNumber),
      facebookPageLink: null,
      canDisconnect: directory.whatsapp.permissions.canDisconnect,
    });
  }
  if (directory.facebook && isChannelSidebarVisible(directory.facebook.state)) {
    rows.push({
      id: "facebook",
      state: directory.facebook.state,
      title: channelCardTitle(
        directory.facebook.page?.localName,
        directory.facebook.page?.pageName ?? t("channels.platforms.facebook"),
      ),
      secondary: directory.facebook.page?.pageName?.trim() || null,
      providerId: directory.facebook.page?.pageId ?? null,
      // pageId and pageName are not a documented conversation URL.
      conversationLink: null,
      facebookPageLink: facebookPageUrl(directory.facebook.page?.pageId),
      canDisconnect: directory.facebook.permissions.canDisconnect,
    });
  }
  if (directory.instagram && isChannelSidebarVisible(directory.instagram.state)) {
    const account = directory.instagram.account;
    const username = formatInstagramUsername(account?.username);
    const providerTitle =
      account?.name?.trim() ||
      formatInstagramUsername(account?.username) ||
      t("channels.platforms.instagram");
    const title = channelCardTitle(account?.localName, providerTitle);
    const providerName = account?.name?.trim() || null;
    rows.push({
      id: "instagram",
      state: directory.instagram.state,
      title,
      secondary: username ?? (providerName && providerName !== title ? providerName : null),
      providerId: account?.instagramUserId ?? null,
      conversationLink: instagramConversationUrl(account?.username),
      facebookPageLink: null,
      canDisconnect: directory.instagram.permissions.canDisconnect,
    });
  }
  return rows;
}

function ChannelListCard({
  row,
  menuOpen,
  returnFocusOnClose,
  onMenuOpenChange,
}: {
  row: ChannelRow;
  menuOpen: boolean;
  returnFocusOnClose: boolean;
  onMenuOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const directory = useChannelDirectory();
  const { pushNotification } = useNotifications();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function copyText(value: string, successKey: string): Promise<void> {
    const copied = await writeClipboardText(value);
    if (copied) {
      pushNotification({ message: t(successKey) });
      return;
    }
    pushNotification({ kind: "error", message: t("channels.list.copyFailed") });
  }

  const href =
    row.id === "whatsapp"
      ? "/channels/whatsapp"
      : row.id === "facebook"
        ? "/channels/facebook"
        : "/channels/instagram";
  const conversationLink = row.conversationLink;
  const facebookPageLink = row.facebookPageLink;
  const providerId = row.providerId;

  return (
    <li className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <ChannelMark platformType={PLATFORM_TYPE[row.id]} size="list" />
      <Link to={href} className="grid min-w-0 flex-1 text-start no-underline">
        <span className="truncate text-[0.9375rem] font-semibold text-foreground">{row.title}</span>
        {row.secondary ? (
          <span className="truncate text-start text-[0.75rem] text-muted">
            <LtrValue>{row.secondary}</LtrValue>
          </span>
        ) : (
          <span className="truncate text-[0.75rem] text-muted">{t(`channels.platforms.${row.id}`)}</span>
        )}
      </Link>
      <Badge variant={statusVariant(row.state)}>{t(channelStatusLabelKey(row.state))}</Badge>
      <DropdownMenu open={menuOpen} onOpenChange={onMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="size-8 p-0"
            aria-label={t("channels.list.actions")}
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onCloseAutoFocus={(event) => {
            if (!returnFocusOnClose) event.preventDefault();
          }}
        >
          <DropdownMenuItem data-testid="channels-list-open" onSelect={() => navigate(href)}>
            <Pencil size={16} className="shrink-0" aria-hidden="true" />
            {t("channels.list.open")}
          </DropdownMenuItem>
          {conversationLink ? (
            <DropdownMenuItem
              data-testid="channels-list-copy-link"
              onSelect={() => {
                void copyText(conversationLink, "channels.list.conversationLinkCopied");
              }}
            >
              <LinkIcon size={16} className="shrink-0" aria-hidden="true" />
              {t("channels.list.copyConversationLink")}
            </DropdownMenuItem>
          ) : null}
          {facebookPageLink ? (
            <DropdownMenuItem
              data-testid="channels-list-copy-page-link"
              onSelect={() => {
                void copyText(facebookPageLink, "channels.list.facebookPageLinkCopied");
              }}
            >
              <LinkIcon size={16} className="shrink-0" aria-hidden="true" />
              {t("channels.list.copyFacebookPageLink")}
            </DropdownMenuItem>
          ) : null}
          {providerId ? (
            <DropdownMenuItem
              data-testid="channels-list-copy-id"
              onSelect={() => {
                void copyText(providerId, "channels.list.idCopied");
              }}
            >
              <Copy size={16} className="shrink-0" aria-hidden="true" />
              {t("channels.list.copyId")}
            </DropdownMenuItem>
          ) : null}
          {row.canDisconnect ? (
            <DropdownMenuItem
              className="text-destructive data-[highlighted]:bg-error-muted"
              data-testid="channels-list-disconnect"
              onSelect={() => setConfirm(true)}
            >
              <LogOut size={16} className="shrink-0 rtl:-scale-x-100" aria-hidden="true" />
              {t("channels.list.disconnect")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDisconnectDialog
        open={confirm}
        onOpenChange={setConfirm}
        platformType={PLATFORM_TYPE[row.id]}
        title={t(`channels.list.disconnectTitle.${row.id}`)}
        description={t(`channels.list.disconnectBody.${row.id}`)}
        cancelLabel={t("common.cancel")}
        confirmLabel={t(`channels.list.disconnectConfirm.${row.id}`)}
        pendingLabel={t("channels.list.disconnecting")}
        busy={busy}
        testId={`channels-disconnect-${row.id}`}
        onConfirm={() => {
          setBusy(true);
          const request =
            row.id === "whatsapp"
              ? disconnectWhatsApp(directory.organizationId)
              : row.id === "facebook"
                ? disconnectFacebook(directory.organizationId)
                : disconnectInstagram(directory.organizationId);
          void request
            .then(() => {
              setConfirm(false);
              directory.reload();
            })
            .finally(() => setBusy(false));
        }}
      />
    </li>
  );
}
